/**
 * Admin Dashboard Page
 * Shows preset approval metrics and user activity
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Users,
  Music,
  DollarSign,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  Home,
  Eye,
  ThumbsUp,
  ThumbsDown,
  Activity,
  Package,
  Layers,
  MessageSquare,
  LayoutDashboard,
  Shield,
  ChevronDown,
  ChevronUp,
  Crown,
  User,
} from 'lucide-react';
import ReviewModerationPanel from '../components/admin/ReviewModerationPanel';
import { getBackendUrl } from '../config/api';

const API_URL = getBackendUrl();

// Admin emails - must match backend
const ADMIN_EMAILS = ['turnersouthsyndicate@gmail.com'];

const AdminDashboard = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview'); // overview, reviews
  const [metrics, setMetrics] = useState(null);
  const [pendingPresets, setPendingPresets] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [userStats, setUserStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [showUserList, setShowUserList] = useState(false);
  const [userList, setUserList] = useState([]);
  const [userListLoading, setUserListLoading] = useState(false);

  // Check if user is admin
  const isAdmin = user?.email && ADMIN_EMAILS.includes(user.email.toLowerCase());

  const fetchDashboardData = useCallback(async () => {
    if (!token || !isAdmin) return;

    try {
      const headers = { Authorization: `Bearer ${token}` };
      const adminEmail = user?.email;

      // Fetch all data in parallel
      const [metricsRes, pendingRes, activityRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin-dashboard/metrics?admin_email=${adminEmail}`, { headers }),
        fetch(`${API_URL}/api/admin-dashboard/pending-presets?admin_email=${adminEmail}`, { headers }),
        fetch(`${API_URL}/api/admin-dashboard/recent-activity?admin_email=${adminEmail}`, { headers }),
        fetch(`${API_URL}/api/admin-dashboard/user-stats?admin_email=${adminEmail}`, { headers }),
      ]);

      if (metricsRes.ok) {
        const data = await metricsRes.json();
        setMetrics(data);
      }

      if (pendingRes.ok) {
        const data = await pendingRes.json();
        setPendingPresets(data.presets || []);
      }

      if (activityRes.ok) {
        const data = await activityRes.json();
        setRecentActivity(data.activities || []);
      }

      if (statsRes.ok) {
        const data = await statsRes.json();
        setUserStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token, isAdmin, user?.email]);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/marketplace');
      return;
    }
    fetchDashboardData();
  }, [isAdmin, navigate, fetchDashboardData]);

  const handleApprove = async (presetId) => {
    try {
      const response = await fetch(
        `${API_URL}/api/admin-dashboard/approve-preset/${presetId}?admin_email=${user?.email}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        toast.success('Preset approved!');
        setPendingPresets(prev => prev.filter(p => p.id !== presetId));
        setSelectedPreset(null);
        fetchDashboardData();
      } else {
        toast.error('Failed to approve preset');
      }
    } catch (error) {
      toast.error('Error approving preset');
    }
  };

  const handleReject = async (presetId) => {
    const reason = prompt('Enter rejection reason:', 'Does not meet community guidelines');
    if (!reason) return;

    try {
      const response = await fetch(
        `${API_URL}/api/admin-dashboard/reject-preset/${presetId}?admin_email=${user?.email}&reason=${encodeURIComponent(reason)}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        toast.success('Preset rejected');
        setPendingPresets(prev => prev.filter(p => p.id !== presetId));
        setSelectedPreset(null);
        fetchDashboardData();
      } else {
        toast.error('Failed to reject preset');
      }
    } catch (error) {
      toast.error('Error rejecting preset');
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDashboardData();
  };

  const fetchUserList = async () => {
    if (userList.length > 0) {
      setShowUserList(!showUserList);
      return;
    }
    setUserListLoading(true);
    setShowUserList(true);
    try {
      const res = await fetch(
        `${API_URL}/api/admin-dashboard/users?admin_email=${user?.email}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setUserList(data.users || []);
      }
    } catch (e) {
      toast.error('Failed to load user list');
    } finally {
      setUserListLoading(false);
    }
  };

  if (!isAdmin) {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-indigo-500 mx-auto mb-4" />
          <p className="text-zinc-400">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-zinc-900/80 border-b border-zinc-800 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/marketplace')}
              className="text-zinc-400"
            >
              <Home className="w-4 h-4 mr-2" />
              Back to Marketplace
            </Button>
            <div className="h-6 w-px bg-zinc-700" />
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                <Activity className="w-4 h-4 text-white" />
              </div>
              <h1 className="text-xl font-bold">Admin Dashboard</h1>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            className="border-zinc-700"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
        
        {/* Admin Tabs */}
        <div className="border-b border-zinc-800 px-4">
          <nav className="flex gap-6 max-w-7xl mx-auto" role="tablist">
            <button
              role="tab"
              aria-selected={activeTab === 'overview'}
              onClick={() => setActiveTab('overview')}
              className={`py-3 px-1 relative text-sm font-medium transition-colors ${
                activeTab === 'overview'
                  ? 'text-amber-400'
                  : 'text-zinc-500 hover:text-zinc-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <LayoutDashboard className="w-4 h-4" />
                Overview
              </div>
              {activeTab === 'overview' && (
                <motion.div
                  layoutId="adminTab"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber-400"
                />
              )}
            </button>
            <button
              role="tab"
              aria-selected={activeTab === 'reviews'}
              onClick={() => setActiveTab('reviews')}
              className={`py-3 px-1 relative text-sm font-medium transition-colors ${
                activeTab === 'reviews'
                  ? 'text-amber-400'
                  : 'text-zinc-500 hover:text-zinc-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Review Moderation
              </div>
              {activeTab === 'reviews' && (
                <motion.div
                  layoutId="adminTab"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber-400"
                />
              )}
            </button>
          </nav>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {activeTab === 'overview' ? (
          <>
        {/* Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {/* Users Card - Clickable */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={fetchUserList}
            className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 cursor-pointer hover:border-blue-500/50 transition-colors"
            data-testid="users-card"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-400" />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-emerald-400 flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" />
                  +{metrics?.users?.new_7d || 0} this week
                </span>
                {showUserList ? (
                  <ChevronUp className="w-4 h-4 text-zinc-400" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-zinc-400" />
                )}
              </div>
            </div>
            <h3 className="text-2xl font-bold">{metrics?.users?.total || 0}</h3>
            <p className="text-sm text-zinc-500">Total Users</p>
            <div className="mt-2 text-xs text-zinc-400">
              {metrics?.users?.active_7d || 0} active in last 7 days
            </div>
          </motion.div>

          {/* Presets Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <Music className="w-5 h-5 text-purple-400" />
              </div>
              {metrics?.presets?.pending > 0 && (
                <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-1 rounded-full">
                  {metrics?.presets?.pending} pending
                </span>
              )}
            </div>
            <h3 className="text-2xl font-bold">{metrics?.presets?.total || 0}</h3>
            <p className="text-sm text-zinc-500">Total Presets</p>
            <div className="mt-2 flex gap-2 text-xs">
              <span className="text-emerald-400">{metrics?.presets?.approved || 0} approved</span>
              <span className="text-red-400">{metrics?.presets?.rejected || 0} rejected</span>
            </div>
          </motion.div>

          {/* Revenue Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-emerald-400" />
              </div>
            </div>
            <h3 className="text-2xl font-bold">
              ${(metrics?.transactions?.revenue_30d || 0).toFixed(2)}
            </h3>
            <p className="text-sm text-zinc-500">Revenue (30 days)</p>
            <div className="mt-2 text-xs text-zinc-400">
              {metrics?.transactions?.last_7d || 0} transactions this week
            </div>
          </motion.div>

          {/* Projects Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                <Layers className="w-5 h-5 text-cyan-400" />
              </div>
            </div>
            <h3 className="text-2xl font-bold">{metrics?.projects?.total || 0}</h3>
            <p className="text-sm text-zinc-500">Total Projects</p>
            <div className="mt-2 text-xs text-zinc-400">
              {metrics?.projects?.active_7d || 0} active this week
            </div>
          </motion.div>
        </div>

        {/* Expanded User List */}
        <AnimatePresence>
          {showUserList && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-8 overflow-hidden"
            >
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
                <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
                  <h2 className="font-semibold flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-400" />
                    All Users ({userList.length})
                  </h2>
                  <Button variant="ghost" size="sm" onClick={() => setShowUserList(false)} className="text-zinc-400">
                    <XCircle className="w-4 h-4" />
                  </Button>
                </div>
                {userListLoading ? (
                  <div className="p-8 text-center">
                    <RefreshCw className="w-6 h-6 animate-spin text-blue-400 mx-auto mb-2" />
                    <p className="text-zinc-500 text-sm">Loading users...</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-zinc-800 text-zinc-400 text-xs uppercase tracking-wider">
                          <th className="text-left p-3 pl-4">User</th>
                          <th className="text-left p-3">Email</th>
                          <th className="text-left p-3">Role</th>
                          <th className="text-left p-3">Tier</th>
                          <th className="text-left p-3">Status</th>
                          <th className="text-left p-3 pr-4">Joined</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userList.map((u, i) => {
                          const tierColors = {
                            studio: 'bg-amber-500/20 text-amber-400',
                            pro: 'bg-purple-500/20 text-purple-400',
                            free: 'bg-zinc-700/50 text-zinc-400',
                          };
                          const tierColor = tierColors[u.plan] || tierColors.free;
                          return (
                            <tr key={u.id || i} className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors">
                              <td className="p-3 pl-4">
                                <div className="flex items-center gap-2">
                                  <div className="w-7 h-7 rounded-full bg-zinc-700 flex items-center justify-center">
                                    {u.role === 'admin' ? (
                                      <Crown className="w-3.5 h-3.5 text-amber-400" />
                                    ) : (
                                      <User className="w-3.5 h-3.5 text-zinc-400" />
                                    )}
                                  </div>
                                  <span className="font-medium text-zinc-200">{u.name}</span>
                                </div>
                              </td>
                              <td className="p-3 text-zinc-400">{u.email}</td>
                              <td className="p-3">
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  u.role === 'admin' ? 'bg-amber-500/20 text-amber-400' : 'bg-zinc-700/50 text-zinc-400'
                                }`}>
                                  {u.role}
                                </span>
                              </td>
                              <td className="p-3">
                                <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${tierColor}`}>
                                  {u.plan}
                                </span>
                              </td>
                              <td className="p-3">
                                <span className={`text-xs ${
                                  u.plan_status === 'ACTIVE' ? 'text-emerald-400' :
                                  u.plan_status === 'APPROVAL_PENDING' ? 'text-amber-400' :
                                  'text-zinc-500'
                                }`}>
                                  {u.plan_status || '-'}
                                </span>
                              </td>
                              <td className="p-3 pr-4 text-zinc-500 text-xs">
                                {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Pending Approvals */}
          <div className="lg:col-span-2">
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
                <h2 className="font-semibold flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-400" />
                  Pending Approvals
                  {pendingPresets.length > 0 && (
                    <span className="bg-amber-500/20 text-amber-400 text-xs px-2 py-0.5 rounded-full">
                      {pendingPresets.length}
                    </span>
                  )}
                </h2>
              </div>
              <div className="max-h-96 overflow-y-auto">
                {pendingPresets.length === 0 ? (
                  <div className="p-8 text-center text-zinc-500">
                    <CheckCircle className="w-12 h-12 mx-auto mb-3 text-emerald-500/50" />
                    <p>All caught up! No pending presets.</p>
                  </div>
                ) : (
                  pendingPresets.map((preset, index) => (
                    <motion.div
                      key={preset.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="p-4 border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-medium">{preset.name}</h3>
                          <p className="text-sm text-zinc-500 line-clamp-1">
                            {preset.description || 'No description'}
                          </p>
                          <div className="flex items-center gap-3 mt-2 text-xs text-zinc-400">
                            <span>By: {preset.creator_name || 'Unknown'}</span>
                            <span>•</span>
                            <span>${(preset.price || 0).toFixed(2)}</span>
                            <span>•</span>
                            <span>{preset.category || 'Uncategorized'}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedPreset(preset)}
                            className="text-zinc-400 hover:text-white"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleApprove(preset.id)}
                            className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/20"
                          >
                            <ThumbsUp className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleReject(preset.id)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                          >
                            <ThumbsDown className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div>
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-zinc-800">
                <h2 className="font-semibold flex items-center gap-2">
                  <Clock className="w-5 h-5 text-indigo-400" />
                  Recent Activity
                </h2>
              </div>
              <div className="max-h-96 overflow-y-auto">
                {recentActivity.length === 0 ? (
                  <div className="p-8 text-center text-zinc-500">
                    <Activity className="w-12 h-12 mx-auto mb-3 text-zinc-600" />
                    <p>No recent activity</p>
                  </div>
                ) : (
                  recentActivity.slice(0, 15).map((activity, index) => (
                    <div
                      key={index}
                      className="px-4 py-3 border-b border-zinc-800/50 text-sm"
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={`w-2 h-2 rounded-full mt-1.5 ${
                            activity.type === 'user_registration'
                              ? 'bg-blue-400'
                              : activity.type === 'preset_submission'
                              ? 'bg-purple-400'
                              : 'bg-emerald-400'
                          }`}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-zinc-300 truncate">{activity.description}</p>
                          {activity.timestamp && (
                            <p className="text-xs text-zinc-500 mt-0.5">
                              {new Date(activity.timestamp).toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Top Creators & Sellers */}
        {userStats && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
            {/* Top Creators */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-zinc-800">
                <h2 className="font-semibold flex items-center gap-2">
                  <Music className="w-5 h-5 text-purple-400" />
                  Top Creators
                </h2>
              </div>
              <div className="divide-y divide-zinc-800/50">
                {(userStats.top_creators || []).slice(0, 5).map((creator, index) => (
                  <div key={index} className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-purple-500/20 text-purple-400 text-xs flex items-center justify-center font-bold">
                        {index + 1}
                      </span>
                      <div>
                        <p className="font-medium">{creator.name || 'Unknown'}</p>
                        <p className="text-xs text-zinc-500">{creator.email}</p>
                      </div>
                    </div>
                    <span className="text-sm text-zinc-400">
                      {creator.preset_count} presets
                    </span>
                  </div>
                ))}
                {(!userStats.top_creators || userStats.top_creators.length === 0) && (
                  <div className="p-8 text-center text-zinc-500">No creators yet</div>
                )}
              </div>
            </div>

            {/* Top Sellers */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-zinc-800">
                <h2 className="font-semibold flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-emerald-400" />
                  Top Sellers
                </h2>
              </div>
              <div className="divide-y divide-zinc-800/50">
                {(userStats.top_sellers || []).slice(0, 5).map((seller, index) => (
                  <div key={index} className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-emerald-500/20 text-emerald-400 text-xs flex items-center justify-center font-bold">
                        {index + 1}
                      </span>
                      <div>
                        <p className="font-medium">{seller.name || 'Unknown'}</p>
                        <p className="text-xs text-zinc-500">{seller.sale_count} sales</p>
                      </div>
                    </div>
                    <span className="text-sm text-emerald-400">
                      ${(seller.total_revenue || 0).toFixed(2)}
                    </span>
                  </div>
                ))}
                {(!userStats.top_sellers || userStats.top_sellers.length === 0) && (
                  <div className="p-8 text-center text-zinc-500">No sales yet</div>
                )}
              </div>
            </div>
          </div>
        )}

      {/* Preset Detail Modal */}
      <AnimatePresence>
        {selectedPreset && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
            onClick={() => setSelectedPreset(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-zinc-900 border border-zinc-800 rounded-xl max-w-lg w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-xl font-bold mb-4">{selectedPreset.name}</h2>
              <div className="space-y-3 text-sm">
                <p><span className="text-zinc-500">Description:</span> {selectedPreset.description || 'None'}</p>
                <p><span className="text-zinc-500">Creator:</span> {selectedPreset.creator_name}</p>
                <p><span className="text-zinc-500">Email:</span> {selectedPreset.creator_email}</p>
                <p><span className="text-zinc-500">Price:</span> ${(selectedPreset.price || 0).toFixed(2)}</p>
                <p><span className="text-zinc-500">Category:</span> {selectedPreset.category || 'Uncategorized'}</p>
              </div>
              <div className="flex gap-3 mt-6">
                <Button
                  onClick={() => handleApprove(selectedPreset.id)}
                  className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleReject(selectedPreset.id)}
                  className="flex-1"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
          </>
        ) : (
          <ReviewModerationPanel isAdmin={isAdmin} />
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
